import { useEntities } from "@app/store/entityStore";
import SearchBar from "@design-system/Inputs/SearchBar";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { useAtom } from "jotai";
import { type FC, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { StringParam, useQueryParam } from "use-query-params";
import { useObjectivesWithDetails } from "../../data";
import { useFilteredObjectives } from "../../hooks";
import ObjectiveTableActionBar from "./actionBar";
import { selectedGroupingTypeAtomFamily } from "./ctx";
import { GroupedTable } from "./GroupedTable";
import { GroupingSelect } from "./GroupingSelect";
import { keys, RESOURCE_NAME } from "./i18n";
import { type Objective } from "./types";
import { UngroupedTable } from "./UngroupedTable";

type ObjectivesTableProps = {
    entityId: string;
};

export const ObjectivesTable: FC<ObjectivesTableProps> = ({ entityId }) => {
    const ungroupedTableRef = useRef<{ unset: () => void }>({
        unset: () => {},
    });

    const [urlSearchString, setUrlSearchString] = useQueryParam(
        "search",
        StringParam,
    );
    const { org_uname } = useParams();

    const [groupingType, setGroupingType] = useAtom(
        selectedGroupingTypeAtomFamily(org_uname),
    );
    const [selectedObjectiveIds, setSelectedObjectiveIds] = useState<string[]>(
        [],
    );

    const { entities } = useEntities();
    const { objectives } = useObjectivesWithDetails(
        {
            entityId,
        },
        { skip: entities?.length != 0 && !entityId },
    );

    const objectivesFiltered = useFilteredObjectives(
        objectives || [],
        urlSearchString || "",
    );

    const selectedObjectives = useMemo(() => {
        return selectedObjectiveIds
            .map((id) => {
                return objectivesFiltered.find(
                    (objective) => objective.id === id,
                );
            })
            .filter((elt) => elt) as Objective[];
    }, [selectedObjectiveIds, objectivesFiltered]);

    const unset = ungroupedTableRef.current?.unset ?? (() => {});

    const { t } = useTranslation(RESOURCE_NAME);

    return (
        <FlexCol w="full" gap="4">
            <FlexRow gap="3" alignItems="center">
                <SearchBar
                    searchString={urlSearchString || ""}
                    setSearchString={setUrlSearchString}
                    placeholder={t(keys.search_placeholder)}
                    className="w-[200px]"
                />
                <GroupingSelect
                    value={groupingType}
                    onChange={setGroupingType}
                />
            </FlexRow>
            {groupingType === "subtheme" ? (
                <GroupedTable
                    entityId={entityId}
                    onSelectionChange={setSelectedObjectiveIds}
                    ref={ungroupedTableRef}
                />
            ) : (
                <UngroupedTable
                    entityId={entityId}
                    onSelectionChange={setSelectedObjectiveIds}
                    ref={ungroupedTableRef}
                />
            )}
            <ObjectiveTableActionBar
                objectives={selectedObjectives || []}
                unset={unset}
            />
        </FlexCol>
    );
};
